import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Button,
  Input,
  Label,
  FormGroup,
  Row,
  Col,
  CustomInput,
  Card,
  CardHeader,
  Collapse,
  CardBody,
  Badge,
} from "reactstrap";
import {
  deepClone,
  errorHandler,
  getFeatureStatus,
  getFullName,
  showToast,
  splitFullName,
} from "../../helper-methods";
import { permissionSettingsConfig } from "../../config/memberPermissionConfig";
import { RegexConfig } from "../../config/RegexConfig";
import { getConfiguredString } from "../../helper-methods/configureString";
import {
  createTeamMember,
  editTeamMember,
  getOneTeamMembersById,
} from "../../http-calls";

const AddMemberModal = ({ isOpen, member, toggle, updateTeamMembersTable }) => {
  const [formFields, setFormFields] = useState({
    name: "",
    email: "",
    phone: "",
    permissionSettings: {
      feed: {},
      liveEvent: {},
      PPV: {},
      vault: {},
      messaging: {},
      profileAndSettings: {},
      shoutOuts: {},
      myTeam: {},
      statement: {},
      Promotion: {},
    },
  });
  const [errors, setErrors] = useState({});
  const [isDirty, setIsDirty] = useState({});
  const [presetValue, setPresetValue] = useState("");
  const [isOpenKey, setIsOpenKey] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const _resetStates = () => {
    setFormFields({
      name: "",
      email: "",
      phone: "",
      permissionSettings: {
        feed: {},
        liveEvent: {},
        PPV: {},
        vault: {},
        messaging: {},
        profileAndSettings: {},
        shoutOuts: {},
        myTeam: {},
        statement: {},
        Promotion: {},
      },
    });
    setPresetValue("");
    setIsOpenKey("");
    setErrors({});
    setIsDirty({});
    setIsLoading(false);
  };

  const _toggleCollapse = (isOpenKey) => {
    setIsOpenKey((prev) => (prev !== isOpenKey ? isOpenKey : null));
  };

  const _closeModal = () => {
    _resetStates();
    toggle();
  };

  const _getOneTeamMembersById = async () => {
    try {
      setIsLoading(true);

      if (!member || !member._id) {
        _closeModal();
        showToast("Something went wrong. Try again after sometime.", "error");
        return;
      }

      const res = await getOneTeamMembersById(member?._id);
      const newFormFields = { ...formFields };
      newFormFields.name = getFullName(res?.member?.name);
      newFormFields.email = res?.member?.email;
      newFormFields.phone = res?.member?.phone;
      newFormFields.permissionSettings = res?.member?.permissionSettings;

      setFormFields(newFormFields);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      _closeModal();
      errorHandler(error);
    }
  };

  const _validateForm = ({ newFormFields, newIsDirty }) => {
    const newErrors = { ...errors };
    return new Promise((resolve) => {
      let isFormValid = true;
      Object.keys(newFormFields).forEach((key) => {
        if (newIsDirty[key]) {
          switch (key) {
            case "name": {
              if (newFormFields?.[key]?.trim()?.length) {
                if (
                  RegexConfig?.[key]?.test(
                    String(newFormFields?.[key]).toLowerCase()
                  )
                ) {
                  newErrors[key] = null;
                  newIsDirty[key] = false;
                } else {
                  newErrors[key] =
                    "Invalid Name, It must start with a character";
                  isFormValid = false;
                }
              } else {
                newErrors[key] = "Required";
                isFormValid = false;
              }

              break;
            }

            case "email": {
              if (newFormFields?.[key]?.trim()?.length) {
                if (
                  RegexConfig?.[key]?.test(
                    String(newFormFields[key]).toLowerCase()
                  )
                ) {
                  newErrors[key] = null;
                  newIsDirty[key] = false;
                } else {
                  newErrors[key] = "Invalid Email";
                  isFormValid = false;
                }
              } else {
                newErrors[key] = "Email is Required";
                isFormValid = false;
              }

              break;
            }

            case "phone": {
              if (newFormFields?.[key]?.trim()?.length) {
                if (
                  RegexConfig?.[key]?.test(
                    String(newFormFields[key]).toLowerCase()
                  )
                ) {
                  newErrors[key] = null;
                  newIsDirty[key] = false;
                } else {
                  newErrors[key] = "Invalid Phone";
                  isFormValid = false;
                }
              } else {
                newErrors[key] = "Phone is Required";
                isFormValid = false;
              }

              break;
            }

            default:
          }
        }
      });
      setErrors(newErrors);
      setIsDirty(newIsDirty);
      resolve(isFormValid);
    });
  };

  // marking all formfields dirty
  const _markAllIsDirty = () => {
    return new Promise((resolve) => {
      const newIsDirty = { ...isDirty };
      const newFormFields = { ...formFields };
      Object.keys(newFormFields).forEach((key) => {
        newIsDirty[key] = true;
      });
      setIsDirty(newIsDirty);
      resolve(newIsDirty);
    });
  };

  const _udpateFieldValue = (key, value) => {
    const newFormFields = { ...formFields };
    newFormFields[key] = value;
    setFormFields(newFormFields);
  };

  const _onBlurHandler = (key) => {
    const newFormFields = { ...formFields };
    const newIsDirty = { ...isDirty };
    newIsDirty[key] = true;
    setIsDirty(newIsDirty);
    _validateForm({ newFormFields, newIsDirty });
  };

  const _filterPermissionSettingsConfig = () => {
    const isLiveEventActive = getFeatureStatus("event");
    const isPayPerViewActive = getFeatureStatus("ppv");
    const isShoutoutActive = getFeatureStatus("shoutout");
    const isVaultActive = getFeatureStatus("vault");
    const isStoreActive = getFeatureStatus("store");

    return permissionSettingsConfig.filter((each) => {
      if (each.heading.string === "vault" && !isVaultActive) return null;
      if (each.heading.string === "liveEvent" && !isLiveEventActive)
        return null;
      if (each.heading.string === "PPV" && !isPayPerViewActive) return null;
      if (each.heading.string === "store" && !isStoreActive) return null;
      if (each.heading.string === "shoutOuts" && !isShoutoutActive) return null;
      return each;
    });
  };

  const _presetPermission = (presetValue = "") => {
    let newFormFields = deepClone(formFields);

    switch (presetValue) {
      case "lite": {
        const filterPermissionSettingsConfig =
          _filterPermissionSettingsConfig();

        filterPermissionSettingsConfig.forEach((each) => {
          let permissions = {};
          each.values.forEach((subEach) => {
            if (subEach.preset?.includes(presetValue))
              permissions[subEach.string] = true;
          });
          newFormFields.permissionSettings[each.heading.string] = {
            ...permissions,
          };
        });

        break;
      }

      case "manager": {
        const filterPermissionSettingsConfig =
          _filterPermissionSettingsConfig();

        filterPermissionSettingsConfig.forEach((each) => {
          let permissions = {};
          each.values.forEach((subEach) => {
            if (subEach.preset?.includes(presetValue))
              permissions[subEach.string] = true;
          });
          newFormFields.permissionSettings[each.heading.string] = {
            ...permissions,
          };
        });

        break;
      }

      case "partner": {
        const filterPermissionSettingsConfig =
          _filterPermissionSettingsConfig();

        filterPermissionSettingsConfig.forEach((each) => {
          let permissions = {};
          each.values.forEach((subEach) => {
            permissions[subEach.string] = true;
          });
          newFormFields.permissionSettings[each.heading.string] = {
            ...permissions,
          };
        });

        break;
      }

      default: {
        newFormFields.permissionSettings = {
          feed: {},
          liveEvent: {},
          PPV: {},
          vault: {},
          messaging: {},
          profileAndSettings: {},
          shoutOuts: {},
          store: {},
          myTeam: {},
          statement: {},
          Promotion: {},
        };
        break;
      }
    }
    setFormFields(newFormFields);
    setPresetValue(presetValue);
  };

  const _selectedIndividualPermissionCount = (permissionName) => {
    try {
      let count = 0;

      const permissions = formFields.permissionSettings?.[permissionName];
      if (!permissions) return 0;

      Object.values(permissions).forEach((each) =>
        each ? (count = count + 1) : ""
      );
      return count;
    } catch (error) {
      console.log("error>>", error);
      return 0;
    }
  };

  /**
   * @param {String} permissionName - i.e. feed / vault / store
   * @param {String} permissionType - i.e. canView / canEdit / canDelete
   * @param {Boolean} value - true / false - i.e. checked or not
   */
  const _onChangeCheckbox = (permissionName, permissionType, value) => {
    const newFormFields = deepClone(formFields);

    const findConfig = permissionSettingsConfig.find(
      (each) => each.heading.string === permissionName
    );

    // set view-permission `true`; if any other permission is being set `true`
    if (
      findConfig?.values?.[0]?.string &&
      findConfig.values[0].string !== permissionType &&
      value
    ) {
      newFormFields.permissionSettings[permissionName][
        findConfig.values[0].string
      ] = true;
    }

    // remove all permission, if view-permission itself is being set `false`
    if (
      findConfig?.values?.[0]?.string &&
      findConfig.values[0].string === permissionType &&
      !value
    ) {
      newFormFields.permissionSettings[permissionName] = {};
    }

    newFormFields.permissionSettings[permissionName][permissionType] = value;
    setFormFields(newFormFields);
    setPresetValue("");
  };

  const _handleSubmit = async (e) => {
    if (e) e.preventDefault();
    try {
      setIsLoading(true);
      const newFormFields = { ...formFields };
      const newIsDirty = await _markAllIsDirty();
      const isFormValid = await _validateForm({ newFormFields, newIsDirty });

      if (!isFormValid) {
        setIsLoading(false);
        showToast("Fill Fields correctly");
        return;
      }

      // Format name
      const { firstName, lastName } = splitFullName(formFields?.name);

      const memberData = {
        name: {
          first: firstName,
          last: lastName,
        },
        email: newFormFields.email.trim(),
        phone: newFormFields.phone.trim(),
        permissionSettings: newFormFields.permissionSettings,
      };

      if (member?._id) {
        await editTeamMember(member._id, memberData);
        showToast("Member updated successfully", "success");
      } else {
        await createTeamMember(memberData);
        showToast("Member added successfully", "success");
      }
      _closeModal();
      updateTeamMembersTable();
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      errorHandler(error);
    }
  };

  const filterPermissionSettingsConfig = _filterPermissionSettingsConfig();

  useEffect(() => {
    if (member && isOpen) {
      _getOneTeamMembersById();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, member]);

  return (
    <Modal
      isOpen={isOpen}
      toggle={() => _closeModal()}
      className="modal-dialog-centered modal-dialog-scrollable"
    >
      <ModalHeader toggle={() => _closeModal()}>
        {member && member._id ? "Update" : "Add"} Team Member{" "}
        {isLoading ? <i className="fa fa-spinner fa-spin mr-1" /> : null}
      </ModalHeader>

      <ModalBody className="teamMemberModal">
        <FormGroup>
          <Label>Name</Label>
          <Input
            type="text"
            placeholder="Enter"
            value={formFields.name}
            onChange={(e) => _udpateFieldValue("name", e.target.value)}
            onBlur={() => _onBlurHandler("name")}
          />
          {errors?.name ? (
            <div className="validation-error">{errors.name}</div>
          ) : null}
        </FormGroup>

        <Row>
          <Col sm={6} className="pr-sm-1">
            <FormGroup>
              <Label>Email</Label>
              <Input
                type="email"
                placeholder="Enter"
                value={formFields.email}
                onChange={(e) => _udpateFieldValue("email", e.target.value)}
                onBlur={() => _onBlurHandler("email")}
              />
              {errors.email ? (
                <div className="validation-error">{errors.email}</div>
              ) : null}
            </FormGroup>
          </Col>
          <Col sm={6} className="pl-sm-1">
            <FormGroup>
              <Label>Phone</Label>
              <Input
                type="text"
                placeholder="Enter"
                value={formFields.phone}
                onChange={(e) => _udpateFieldValue("phone", e.target.value)}
                onBlur={() => _onBlurHandler("phone")}
              />
              {errors.phone ? (
                <div className="validation-error">{errors.phone}</div>
              ) : null}
            </FormGroup>
          </Col>
        </Row>

        <div className="d-flex justify-content-between align-items-center mb-2">
          <Label className="mb-0">Permissions</Label>

          <Input
            type="select"
            value={presetValue}
            onChange={(e) => _presetPermission(e.target.value)}
            style={{ width: 125 }}
          >
            <option value="">Select</option>
            {/* Lite (limited access) */}
            <option value="lite">Lite</option>
            {/* Manager (most access) */}
            <option value="manager">Manager</option>
            {/* Partner (full access) */}
            <option value="partner">Partner</option>
          </Input>
        </div>

        {filterPermissionSettingsConfig.map((each, index) => {
          return (
            <div id="accordion" key={`${index}_${each?.heading?.string}`}>
              <Card>
                <CardHeader
                  onClick={() => _toggleCollapse(each?.heading?.string)}
                >
                  <div className="d-flex align-items-center">
                    <h5 className="faqQues">
                      {`${index + 1}. ${getConfiguredString(
                        each?.heading?.label
                      )}`}
                    </h5>
                    {_selectedIndividualPermissionCount(each.heading.string) ? (
                      <Badge className="memberPermissionBadge">
                        {_selectedIndividualPermissionCount(
                          each.heading.string
                        )}
                      </Badge>
                    ) : null}
                  </div>

                  {isOpenKey && each.heading.string === isOpenKey ? (
                    <i className="fa fa-chevron-up" />
                  ) : (
                    <i className="fa fa-chevron-down" />
                  )}
                </CardHeader>

                <Collapse isOpen={each.heading.string === isOpenKey}>
                  <CardBody>
                    {React.Children.toArray(
                      each.values.map((subEach, subIndex) => {
                        return (
                          <CustomInput
                            type="checkbox"
                            className="newMemberPermission"
                            checked={
                              formFields.permissionSettings?.[
                                each?.heading?.string
                              ]?.[subEach?.string]
                                ? true
                                : false
                            }
                            onChange={(e) => {
                              _onChangeCheckbox(
                                each.heading.string,
                                subEach.string,
                                e.target.checked
                              );
                            }}
                            id={`${each.heading.string}_${subEach.string}`}
                            label={getConfiguredString(subEach.label)}
                          />
                        );
                      })
                    )}
                  </CardBody>
                </Collapse>
              </Card>
            </div>
          );
        })}
      </ModalBody>

      <ModalFooter>
        <Button className="modalBtnCancel" onClick={() => _closeModal()}>
          Cancel
        </Button>
        <Button
          className="modalBtnSave"
          onClick={(e) => _handleSubmit(e)}
          disabled={isLoading}
        >
          {isLoading ? <i className="fa fa-spinner fa-spin mr-1" /> : null}{" "}
          {member?._id ? "Update Member" : "Add Member"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AddMemberModal;
